/**
 * Permission utilities for role-based and permission-based access control
 * Provides helper functions for checking user permissions and roles
 *
 * This module supports both:
 * 1. Legacy role-based checks (using user.isAdministrator)
 * 2. New permission-based checks (using permissions array from backend)
 */

import { LawFirmUser } from '@/lib/api/account-api';

// ============================================================================
// Permission Constants
// ============================================================================

export const PERMISSIONS = {
  ACCOUNT_MANAGE: 'account:manage',
  BILLING_MANAGE: 'billing:manage'
} as const;

/**
 * Canonical backend role for an accounts-team billing-only contact (LF-3963).
 */
export const BILLING_ONLY_ROLE = 'billing_only';

/**
 * Canonical backend role for an ordinary firm user who ALSO manages billing
 * (VALE-435). Carries `billing:manage` without `account:manage`, and — unlike
 * `billing_only` — is not confined to the billing area: they keep full
 * non-admin access to the app, and count as a billable seat like any `user`.
 */
export const BILLING_USER_ROLE = 'billing_user';

/**
 * The values `law_firm_users.role` is allowed to hold, mirroring the backend's
 * `LawFirmRole` enum. The column is free-text `varchar` with no CHECK
 * constraint, so this list is the frontend's own contract rather than something
 * the database enforces — which is also why `getRoleLabel` below has to cope
 * with a value outside it.
 *
 * The single source of truth: both the union type and the runtime membership
 * test are derived from it.
 */
export const CANONICAL_LAW_FIRM_ROLES = [
  'admin',
  'user',
  BILLING_USER_ROLE,
  BILLING_ONLY_ROLE
] as const;

export type CanonicalLawFirmRole = (typeof CANONICAL_LAW_FIRM_ROLES)[number];

/**
 * Human-readable label per canonical role, for every surface that displays a
 * firm user's role. One map, so the team list and the profile card cannot
 * drift — and because the raw column values (`billing_only`, `billing_user`)
 * are not presentable to a user.
 */
const ROLE_LABELS: Record<CanonicalLawFirmRole, string> = {
  admin: 'Administrator',
  user: 'User',
  [BILLING_USER_ROLE]: 'User + Billing',
  [BILLING_ONLY_ROLE]: 'Billing'
};

function isCanonicalRole(role: string): role is CanonicalLawFirmRole {
  return (CANONICAL_LAW_FIRM_ROLES as readonly string[]).includes(role);
}

/**
 * The display label for a role string.
 *
 * A canonical role gets its label from the map above. Anything else is a legacy
 * free-text job title ('Solicitor', 'Managing Partner') and is rendered as
 * written — deliberately NOT put through `normalizeRole`, which would flatten
 * every one of them to 'User' and throw away information the firm entered.
 * Neither dev nor prod currently holds such a value, but the column permits it.
 */
export function getRoleLabel(role: string | undefined | null): string {
  const raw = role?.trim();
  if (!raw) return ROLE_LABELS.user;

  const canonical = raw.toLowerCase();
  return isCanonicalRole(canonical) ? ROLE_LABELS[canonical] : raw;
}

/**
 * Canonical backend status for a deactivated (locked-out) user (LF-4271).
 */
export const DEACTIVATED_STATUS = 'deactivated';

/**
 * Canonical backend status for a law firm user who has been created but has not
 * yet completed setup. Distinct from the FIRM's `membershipStatus: 'pending'`,
 * which is a commercial state about the account rather than about the person.
 */
export const PENDING_STATUS = 'pending';

// ============================================================================
// Role Normalization
// ============================================================================

/**
 * Normalize a role string to a canonical backend role, mapping the legacy
 * job-title variations ('solicitor', 'partner', …) onto 'admin' / 'user'.
 *
 * The two billing roles are matched EXACTLY, and before the substring scan
 * below — which is not incidental. `'billing_user'.includes('user')` is true,
 * so the scan collapsed `billing_user` to `user` and silently stripped the
 * billing grant on every round-trip through this helper (VALE-435). Matching
 * `billing_only` exactly is the same fix applied for consistency: it used to
 * fall through to the 'user' default.
 */
export function normalizeRole(
  role: string | undefined | null
): CanonicalLawFirmRole {
  if (!role) return 'user';

  const normalizedRole = role.toLowerCase().trim();

  if (normalizedRole === BILLING_USER_ROLE) return BILLING_USER_ROLE;
  if (normalizedRole === BILLING_ONLY_ROLE) return BILLING_ONLY_ROLE;

  // Map common variations to valid values
  const adminVariations = [
    'admin',
    'administrator',
    'owner',
    'partner',
    'principal'
  ];
  const userVariations = [
    'user',
    'member',
    'team member',
    'solicitor',
    'lawyer',
    'attorney',
    'paralegal',
    'staff'
  ];

  if (adminVariations.some((variation) => normalizedRole.includes(variation))) {
    return 'admin';
  }

  if (userVariations.some((variation) => normalizedRole.includes(variation))) {
    return 'user';
  }

  // Default to 'user' for any unrecognized role
  return 'user';
}

// ============================================================================
// Permission-Based Functions (New System)
// ============================================================================

/**
 * Check if a user has a specific permission
 * Uses the permissions array from the backend
 */
export function hasPermission(
  permissions: string[] | undefined,
  requiredPermission: string
): boolean {
  if (!permissions) return false;
  return permissions.includes(requiredPermission);
}

/**
 * Check if user can manage account settings using permissions array
 */
export function canManageAccount(permissions: string[] | undefined): boolean {
  return hasPermission(permissions, PERMISSIONS.ACCOUNT_MANAGE);
}

/**
 * Detect an accounts-team billing-only contact (LF-3963). These users are
 * restricted to the billing page and must be kept out of the rest of the app
 * (onboarding, setup, matters, etc.).
 *
 * Determined by the ROLE ALONE. `billing:manage` used to serve as a secondary
 * signal here, on the reasoning that only billing-only contacts ever carried
 * it. `billing_user` (VALE-435) breaks that premise — it carries the same
 * permission and must NOT be confined — so the permission can no longer stand
 * in for the role. The `permissions` parameter was **removed** rather than
 * ignored, deliberately: that makes every call site a compile error and forces
 * each one to be re-checked, which is how the three sidebar call sites passing
 * `undefined` for the user (and relying purely on the permission) were found.
 *
 * The corollary is that callers must pass a user object carrying a real `role`.
 * One without a role reads as "not billing-only" — the permissive direction —
 * so thread the role through rather than dropping it.
 */
export function isBillingOnly(
  user: { role?: string | null } | null | undefined
): boolean {
  return user?.role?.toLowerCase().trim() === BILLING_ONLY_ROLE;
}

/**
 * Detect a deactivated law firm user (LF-4271). The backend rejects these users
 * (403 USER_DEACTIVATED) on every endpoint EXCEPT the profile read, so the FE
 * reads `status` from /profile and renders a full-screen lock-out instead of the
 * app. The backend guard is the hard boundary; this drives the UX layer.
 */
export function isDeactivated(
  user: { status?: string | null } | null | undefined
): boolean {
  return user?.status?.toLowerCase().trim() === DEACTIVATED_STATUS;
}

/**
 * Detect a law firm user who is still `pending` — created, but not yet through
 * setup. Normalised the same way as `isDeactivated` so the two status reads
 * cannot drift on casing or stray whitespace.
 *
 * An absent status reads as NOT pending, which is the conservative direction
 * for every current caller: the activation gate's staff-session skip narrows on
 * this, so an unknown status keeps the gate rather than opening it.
 */
export function isPendingUser(
  user: { status?: string | null } | null | undefined
): boolean {
  return user?.status?.toLowerCase().trim() === PENDING_STATUS;
}

/**
 * Whether a user may manage the firm billing (enter the card, view invoices).
 *
 * Three audiences: admins (`account:manage`), billing-only accounts-team
 * contacts and `billing_user`s (both `billing:manage`). When a permission array
 * is available it is the source of truth for all three.
 *
 * Deliberately no longer spelled `isBillingOnly(user, permissions) ||
 * canManageAccount(permissions)`: that only ever worked because `isBillingOnly`
 * used to answer the `billing:manage` question on the caller's behalf, and it
 * would now exclude `billing_user` — the one role this helper exists to admit.
 */
export function canManageBilling(
  user: { role?: string | null; isAdministrator?: boolean } | null | undefined,
  permissions?: string[]
): boolean {
  if (permissions !== undefined) {
    return (
      hasPermission(permissions, PERMISSIONS.BILLING_MANAGE) ||
      hasPermission(permissions, PERMISSIONS.ACCOUNT_MANAGE)
    );
  }

  // Legacy call shape with no permission array — fall back to the role string
  // and the derived admin flag.
  const role = user?.role?.toLowerCase().trim();
  return (
    role === BILLING_ONLY_ROLE ||
    role === BILLING_USER_ROLE ||
    user?.isAdministrator === true
  );
}

// ============================================================================
// Role-Based Functions (Legacy System - Backward Compatible)
// ============================================================================

/**
 * Check if a user is an administrator
 * @deprecated Use canManageAccount with permissions array instead
 */
export function isAdministrator(user: LawFirmUser | null | undefined): boolean {
  if (!user) return false;
  // Legacy fallback - should use permissions array instead
  return user.isAdministrator === true;
}

/**
 * Check if a user has signature access
 */
export function hasSignatureAccess(
  user: LawFirmUser | null | undefined
): boolean {
  if (!user) return false;
  // Default to true if not explicitly set to false
  return user.hasSignatureAccess !== false;
}

/**
 * Check if a user can edit law firm settings
 * Supports both role-based and permission-based checks
 */
export function canEditLawFirmSettings(
  user: LawFirmUser | null | undefined,
  permissions?: string[]
): boolean {
  // If permissions array is provided, use permission-based check
  if (permissions !== undefined) {
    return canManageAccount(permissions);
  }
  // Fall back to role-based check
  return isAdministrator(user);
}

/**
 * Check if a user can manage team members
 * Supports both role-based and permission-based checks
 */
export function canManageTeamMembers(
  user: LawFirmUser | null | undefined,
  permissions?: string[]
): boolean {
  // If permissions array is provided, use permission-based check
  if (permissions !== undefined) {
    return canManageAccount(permissions);
  }
  // Fall back to role-based check
  return isAdministrator(user);
}

/**
 * Check if a user can view law firm settings
 * All active users can view firm settings
 */
export function canViewLawFirmSettings(
  user: LawFirmUser | null | undefined
): boolean {
  if (!user) return false;
  return user.status === 'active';
}

/**
 * Check if a user can edit their own profile
 * All active users can edit their own profile
 */
export function canEditOwnProfile(
  user: LawFirmUser | null | undefined
): boolean {
  if (!user) return false;
  return user.status === 'active';
}

/**
 * Check if a user can upload letterhead
 * Supports both role-based and permission-based checks
 */
export function canUploadLetterhead(
  user: LawFirmUser | null | undefined,
  permissions?: string[]
): boolean {
  // If permissions array is provided, use permission-based check
  if (permissions !== undefined) {
    return canManageAccount(permissions);
  }
  // Fall back to role-based check
  return isAdministrator(user);
}

/**
 * Check if a user can manage correspondence preferences
 * Supports both role-based and permission-based checks
 */
export function canManageCorrespondencePreferences(
  user: LawFirmUser | null | undefined,
  permissions?: string[]
): boolean {
  // If permissions array is provided, use permission-based check
  if (permissions !== undefined) {
    return canManageAccount(permissions);
  }
  // Fall back to role-based check
  return isAdministrator(user);
}

/**
 * The minimum shape the two display helpers below need: the **nullable** name
 * columns plus the **non-null** email they fall back to.
 *
 * Widened from `LawFirmUser` in LF-6015. `law_firm_users.first_name` /
 * `middle_name` / `last_name` are all `nullable: true` — an invited user who
 * has not accepted yet has none of them — so every surface that renders a firm
 * user needs the same fallback. Typing the parameter structurally lets the
 * leads board's `LeadBoardUser`, the account sidebar's `AccountSidebarUser`,
 * a matter's `RoleUserSummary` and a task's `assignee` all reach for this one
 * canonical helper instead of hand-rolling a guard each. Widening a parameter
 * is backwards-compatible: every existing `LawFirmUser` call site still fits.
 *
 * A record with no `email` (e.g. the AI-scribe `RecordingParticipant`) cannot
 * use these — it has nothing to fall back *to*, so the display decision has to
 * be made at that call site.
 */
export interface DisplayNameUser {
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  email: string;
}

/**
 * Get display name for a user.
 *
 * Nulls are filtered out **before** the join, which is the whole point: a
 * template literal over two null columns renders the literal string
 * `'null null'`, and that is a non-empty truthy string, so a downstream
 * `.trim()` or `|| fallback` can never rescue it (LF-6015).
 */
export function getUserDisplayName(
  user: DisplayNameUser | null | undefined
): string {
  if (!user) return 'Unknown User';

  const parts = [user.firstName, user.middleName, user.lastName].filter(
    Boolean
  );
  return parts.length > 0 ? parts.join(' ') : user.email;
}

/**
 * Get initials for a user — first+last, else first, else the email's initial.
 */
export function getUserInitials(
  user: DisplayNameUser | null | undefined
): string {
  if (!user) return '?';

  const firstInitial = user.firstName?.charAt(0) || '';
  const lastInitial = user.lastName?.charAt(0) || '';

  if (firstInitial && lastInitial) {
    return `${firstInitial}${lastInitial}`.toUpperCase();
  } else if (firstInitial) {
    return firstInitial.toUpperCase();
  } else if (user.email) {
    return user.email.charAt(0).toUpperCase();
  }

  return '?';
}

/**
 * Check if a form field should be disabled based on user permissions
 */
export function isFieldDisabled(
  fieldName: string,
  isEditing: boolean,
  user: LawFirmUser | null | undefined
): boolean {
  // If not editing, all fields are disabled
  if (!isEditing) return true;

  // If no user, disable all fields
  if (!user) return true;

  // Check specific field permissions
  const adminOnlyFields = [
    'lawFirm.name',
    'lawFirm.email',
    'lawFirm.phoneNumber',
    'lawFirm.valeEmail',
    'lawFirm.postalAddress',
    'lawFirm.businessAddress',
    'lawFirm.trustBankAccount',
    'primaryContact',
    'correspondencePreferences',
    'letterhead'
  ];

  // Check if field requires admin permission
  const requiresAdmin = adminOnlyFields.some((field) =>
    fieldName.startsWith(field)
  );

  if (requiresAdmin && !isAdministrator(user)) {
    return true;
  }

  // Check signature-specific fields
  if (fieldName.includes('signature') && !hasSignatureAccess(user)) {
    return true;
  }

  return false;
}

/**
 * Get permission error message for a specific action
 */
export function getPermissionErrorMessage(action: string): string {
  const messages: Record<string, string> = {
    'edit-law-firm': 'Only administrators can edit law firm settings',
    'manage-team': 'Only administrators can manage team members',
    'upload-letterhead': 'Only administrators can upload letterhead',
    'manage-correspondence':
      'Only administrators can manage correspondence preferences',
    'manage-signature': 'You do not have permission to manage signatures',
    'view-restricted': 'You do not have permission to view this content',
    default: 'You do not have permission to perform this action'
  };

  return messages[action] || messages.default;
}

/**
 * Check if a user role is higher than another
 * Used for preventing users from editing higher-level users
 */
export function isHigherRole(userRole: string, targetRole: string): boolean {
  const roleHierarchy: Record<string, number> = {
    Administrator: 3,
    Solicitor: 2,
    'Team Member': 1
  };

  const userLevel = roleHierarchy[userRole] || 0;
  const targetLevel = roleHierarchy[targetRole] || 0;

  return userLevel > targetLevel;
}

/**
 * Check if a user can edit another user
 */
export function canEditUser(
  currentUser: LawFirmUser | null | undefined,
  targetUser: LawFirmUser | null | undefined
): boolean {
  if (!currentUser || !targetUser) return false;

  // Users cannot edit themselves through team management
  if (currentUser.id === targetUser.id) return false;

  // Only administrators can edit other users
  if (!isAdministrator(currentUser)) return false;

  // Administrators can now edit other administrators
  // This allows for better team management
  return true;
}

/**
 * Get available actions for a user on another user.
 *
 * **Every action here must be one the backend accepts from the target's current
 * status.** The two constraints it mirrors are
 * `backend/src/resources/utils/user-status-transition.util.ts`'s
 * `ALLOWED_TRANSITIONS` and `AccountUsersService`'s own status preconditions —
 * `lib/utils/permissions.test.ts` restates both and fails if this drifts from
 * them. Offering a gesture the backend refuses is how VALE-171 shipped.
 *
 * `toggle-admin` is a *role* write rather than a status one, so it is available
 * from every status — but since VALE-445 it is no longer performed from the team
 * list's overflow menu: role is a field of the record, so it is edited on the
 * team drawer's `Role` row like every other field. It stays in this list because
 * it is still an action an administrator may take on this user.
 */
export function getAvailableUserActions(
  currentUser: LawFirmUser | null | undefined,
  targetUser: LawFirmUser | null | undefined
): string[] {
  const actions: string[] = [];

  if (!currentUser || !targetUser || !isAdministrator(currentUser)) {
    return actions;
  }

  // Cannot perform actions on self
  if (currentUser.id === targetUser.id) {
    return actions;
  }

  // Send invitation for pending users
  if (targetUser.status === 'pending') {
    actions.push('send-invitation');
  }

  // Resend invitation for invited users
  if (targetUser.status === 'invited') {
    actions.push('resend-invitation');
  }

  // Toggle admin for all users (including other admins)
  // This allows admins to manage other admins
  actions.push('toggle-admin');

  // Billing access as a standalone grant (VALE-435): promote a plain `user` to
  // `billing_user`, or demote back. Withheld from administrators, whose
  // `account:manage` already opens billing — offering it would only invite the
  // impression that roles compose, which they do not. Withheld from
  // billing-only contacts too: their confinement is a separate model and
  // explicitly out of scope.
  const targetRole = normalizeRole(targetUser.role);
  if (targetRole !== 'admin' && targetRole !== BILLING_ONLY_ROLE) {
    actions.push('toggle-billing');
  }

  // A member who has never set a password was never active, so there is nothing
  // to deactivate — the backend status machine refuses both `pending` and
  // `invited` → `deactivated` (see
  // backend/src/resources/utils/user-status-transition.util.ts).
  //
  // An `invited` member has a live invitation, so offer revoking it instead. A
  // `pending` member has no invitation to revoke either — the backend rejects
  // revoking anything not already `invited` — so Send Invitation (pushed above)
  // is the only lifecycle action available.
  //
  // `deactivated` gets the reverse gesture (VALE-445): `deactivated` → `active`
  // is the one transition allowed out of it, and it is served by its own endpoint
  // (`POST /account/users/:id/reactivate`) because it has to re-enable the
  // Keycloak credential — a `status` write on the generic PUT would move the row
  // and leave the login broken. It used to be offered `delete`, which the state
  // machine treats as a same-status no-op: a menu item that does nothing.
  if (targetUser.status === 'invited') {
    actions.push('revoke-invitation');
  } else if (targetUser.status === 'active') {
    actions.push('delete');
  } else if (targetUser.status === 'deactivated') {
    actions.push('reactivate');
  }

  return actions;
}
